import { useState, useEffect, useCallback, useRef } from "react";
import { useMemberState } from "../../contexts/membercontext";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {useLoadAuthUser, useLoadCommunity} from "../../hooks/authhooks"; 
import { useLoadMemberships } from "../../hooks/memberhooks";
import { AccountSchema, CommunityOptionType, DSCommunitySchema, ResourceType, SettingsMenuTabType } from "../../data/types";
import { areObjectsEqual, getChangedAttributes } from "../../utils/helpers";
import { useAuthState } from '../../contexts/authcontext'
import { ConfirmationModal } from "../../molecules/AppModal";
import LoadingScreen from "../shared/LoadingScreen";
import { useUpdatePreferences } from "../../hooks/communityhook";
import UserAvatar from "../../components/UserAvatar";
import TeamRoles from "../../molecules/TeamRoles"
import { useManageTeam } from "../../hooks/communityhook";
import { capitalizeString } from "../../utils/helpers";
import { toast } from "sonner";
import { ReactComponent as StripeLogo } from "../../icons/StripeLogo.svg";
import { ReactComponent as QuickBooksIcon } from "../../icons/QuickBooksIcon.svg";
import { ReactComponent as LinkIcon } from "../../icons/LinkIcon.svg";
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { ReactComponent as GoogleCalendarIcon } from "../../icons/GoogleCalIcon.svg";
import { ReactComponent as MailChimpIcon } from "../../icons/mailchimp.svg";
import { ReactComponent as GoogleDriveIcon } from "../../icons/GoogleDriveIcon.svg";
// import { ReactComponent as GoogleSheetsIcon } from "../../icons/GoogleSheetsIcon.svg";
import { ReactComponent as HubspotIcon } from "../../icons/HubspotIcon.svg";
import { ReactComponent as AiriWheelsIcon } from "../../icons/AiriWheelsIcon.svg";
// import { ReactComponent as HubSpotNewIcon } from "../../icons/HubSpotNewIcon.svg";
import IntegrationModal from "../../components/integrations/IntegrationModal";
import DisconnectConfirmationModal from "../../components/integrations/DisconnectConfirmationModal";
import { getAllIntegrationStatuses, getPartnerIntegrationStatus, completeQuickBooksOAuth, completeGoogleCalendarOAuth, completeGoogleDriveOAuth, completeMailchimpOAuth, completeHubSpotOAuth, disconnectQuickBooks } from "../../api/integrations";
import { useSearchParams } from "react-router-dom";



// type SettingsTabType = "profile" | "resources_offered" | "team" | "notifications" | "integrations"
type SettingsPageProps = {
  defaultTab?: SettingsMenuTabType
}
const SettingsPage: React.FC<SettingsPageProps> = ({defaultTab}) => {

  var { authUser } = useAuthState()
  var communityId = (authUser && authUser.primary_community) ? authUser.primary_community : ""
  var { primaryCommunity } = useLoadCommunity(communityId)

  var { memberships } = useLoadMemberships();
  const { membershipRequests } = useMemberState();


  const settingsOptions: {id: SettingsMenuTabType, name: string}[] = [
    {
      id: "profile",
      name: "Profile"
    },
    {
      id: "team",
      name: "Team Roles"
    },
    {
      id: "resources_offered",
      name: "Resources Offered"
    },
    {
      id: "notifications",
      name: "Notifications"
    },
    {
      id: "integrations",
      name: "Integrations"
    }
  ]

  const defaultOption = defaultTab ? settingsOptions.find(option => option.id === defaultTab) : settingsOptions[0]

  const [currentOption, setCurrentOption] = useState<CommunityOptionType>(defaultOption as CommunityOptionType);  // default to profile
  const [currentCommunity, setCurrentCommunity] = useState<DSCommunitySchema>(primaryCommunity as DSCommunitySchema)
  // const [isOpenPlan, setIsOpenPlan] = useState<boolean>(false);
  // const { memberships } = useLoadMemberships();
  // const { membershipRequests } = useLoadRequests();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    if (name && (value || value === "")){
      setCurrentCommunity(prev => (
        {
          ...prev,
          [name]: value
        }
      ))
    }
  }

  useEffect(() => {
    if (primaryCommunity) {
      setCurrentCommunity(primaryCommunity as DSCommunitySchema)
    }
  }, [primaryCommunity]);

  useEffect(() => {
    // document.title = `HomeCook | ${capitalizeString(currentOption.id)} Settings`;
    // eslint-disable-next-line
  }, [memberships, membershipRequests]);

  return (
    <div className="page-canvas h-auto">
      <div className="flex flex-row bg-white h-[75px] w-full">
        {
          settingsOptions.map((option, i) => {
            return (
              <div 
                key={i} 
                onClick={()=>setCurrentOption(option)} 
                className={`${(currentOption.id === option.id) ? 'text-white bg-primary-200 font-medium' : 'text-[#4D4D4D] '} ml-6 my-auto text-[16px] font-raleway border-[1px] border-[#E9E9E9] py-2 px-4 rounded-md cursor-pointer`}
              >
                  {option.name}
              </div>
            )
          })
        }
      </div>
      {
        (currentOption.id === "notifications") ? (
          <NotificationSettings currentCommunity={primaryCommunity as DSCommunitySchema} community={currentCommunity} handleChange={handleChange} />
        ) : currentOption.id === "resources_offered" ? (
          <ResourceOfferedSettings currentCommunity={primaryCommunity as DSCommunitySchema} community={currentCommunity} handleChange={handleChange} />
        ) : currentOption.id === "profile" ? (
          <ProfileSettings authUser={authUser as AccountSchema} handleChange={handleChange} />
        ) : currentOption.id === "team" ? (
          <TeamRoles currentCommunity={primaryCommunity as DSCommunitySchema} community={currentCommunity} handleChange={handleChange} />
        ) : currentOption.id === "integrations" ? (
          <IntegrationsSettings currentCommunity={primaryCommunity as DSCommunitySchema} community={currentCommunity} handleChange={handleChange} />
        ) : (
          <></>
        )
      }

      {/* <AddPlanModal isOpen={isOpenPlan} closeAppModal={()=> setIsOpenPlan(false)} /> */}
    </div>
  );
};


type IntegrationsSettingsProps = {
  currentCommunity: DSCommunitySchema
  community: DSCommunitySchema
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void
}

type IntegrationType = {
  id: string
  name: string
  description: string
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  url: string
  connected: boolean
  accountName?: string
  lastSyncedAt?: string
  connectedAt?: string
  error?: string // Backend may return error or warning field - mapped here for display
}

// Integration configuration - easily enable/disable integrations
// Set to false to hide an integration from the UI
// Example: To disable Mailchimp, set mailchimp: false
// Export for testing purposes
export const INTEGRATION_CONFIG: Record<string, boolean> = {
  stripe: true,
  quickbooks: true,
  google_calendar: false,
  google_drive: false,
  mailchimp: false,
  hubspot: false,
  airiwheels: true,
};

// Integration definitions - add new integrations here
// To add a new integration:
// 1. Add its definition to this array
// 2. Add its enabled/disabled state to INTEGRATION_CONFIG above
// 3. Add the integration component to IntegrationModal.tsx
// Export for testing purposes
export const INTEGRATION_DEFINITIONS: Omit<IntegrationType, 'connected'>[] = [
  {
    id: "stripe",
    name: "Stripe",
    description: "Connect your Stripe account to track payments, payouts, and transactions directly from your dashboard.",
    icon: StripeLogo,
    url: "https://stripe.com"
  },
  {
    id: "quickbooks",
    name: "QuickBooks",
    description: "Connect your QuickBooks account to sync invoices, payments, and financial data automatically.",
    icon: QuickBooksIcon,
    url: "https://quickbooks.com"
  },
  {
    id: "google_calendar",
    name: "Google Calendar",
    description: "Connect your Google Calendar account to sync events and bookings directly from your dashboard.",
    icon: GoogleCalendarIcon,
    url: "https://calendar.google.com"
  },
  {
    id: "google_drive",
    name: "Google Drive",
    description: "Connect your Google Drive account to sync files and documents directly from your dashboard.",
    icon: GoogleDriveIcon,
    url: "https://drive.google.com"
  },
  {
    id: "mailchimp",
    name: "Mailchimp",
    description: "Connect your MailChimp account to sync email lists and campaigns directly from your dashboard.",
    icon: MailChimpIcon,
    url: "https://mailchimp.com"
  },
  {
    id: "hubspot",
    name: "HubSpot",
    description: "Connect your HubSpot account to sync contacts and deals directly from your dashboard.",
    icon: HubspotIcon,
    url: "https://hubspot.com"
  },
  {
    id: "airiwheels",
    name: "AiriWheels",
    description: "Connect AiriWheels to send consented referrals. You can update listing details anytime from Listing & eligibility and Partner setup.",
    icon: AiriWheelsIcon,
    url: "https://airiwheels.ai/"
  },
];

// Build defaultIntegrations from configuration
// Only includes integrations that are enabled in INTEGRATION_CONFIG
// Export for testing purposes
export const getDefaultIntegrations = (): IntegrationType[] => {
  return INTEGRATION_DEFINITIONS
    .filter(def => INTEGRATION_CONFIG[def.id] !== false) // Include if enabled (true or undefined)
    .map(def => ({
      ...def,
      connected: false
    }));
};

// const defaultIntegrations = getDefaultIntegrations();

const IntegrationsSettings: React.FC<IntegrationsSettingsProps> = ({currentCommunity, community, handleChange}) => {
  const { authUser } = useAuthState();
  const communityId = currentCommunity?.id || "";
  const userId = authUser?.username || "";
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize with defaultIntegrations so component can render while loading
  // Use getDefaultIntegrations() to ensure we get the current enabled integrations
  const [integrations, setIntegrations] = useState<IntegrationType[]>(getDefaultIntegrations());
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const isLoadingStatusesRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const statusUpdateHandledRef = useRef(false);

  const loadIntegrationStatuses = useCallback(async (skipLoadingState = false) => {
    // Prevent multiple simultaneous loads - debounce within 500ms
    const now = Date.now();
    if (isLoadingStatusesRef.current) {
      console.log('IntegrationsSettings: Skipping loadIntegrationStatuses - already loading');
      return;
    }
    
    // Debounce: prevent multiple calls within 500ms
    if (now - lastLoadTimeRef.current < 500) {
      console.log('IntegrationsSettings: Skipping loadIntegrationStatuses - debounced');
      return;
    }
    lastLoadTimeRef.current = now;

    try {
      isLoadingStatusesRef.current = true;
      if (!skipLoadingState) {
        setIsLoading(true);
      }
      const statuses = await getAllIntegrationStatuses(communityId, userId);
      const mergedStatuses = statuses.filter((s) => s.id !== 'airiwheels');

      // AiriWheels lives on partner_integrations — merge Connected from that record.
      try {
        const partnerStatus = await getPartnerIntegrationStatus(communityId, 'airiwheels');
        mergedStatuses.push({
          id: 'airiwheels',
          connected: partnerStatus?.status === 'connected',
          lastSyncedAt: partnerStatus?.last_synced_at || null,
        } as any);
      } catch (partnerError) {
        console.warn('IntegrationsSettings: AiriWheels partner status unavailable', partnerError);
        mergedStatuses.push({ id: 'airiwheels', connected: false } as any);
      }
      
      console.log('IntegrationsSettings: Loaded statuses', mergedStatuses);
      
      // Map statuses to integrations
      // Backend may return 'warning' field for non-critical issues - this is handled in getAllIntegrationStatuses
      // and mapped to 'error' field for consistent display
      // Use getDefaultIntegrations() to get current enabled integrations
      const currentDefaultIntegrations = getDefaultIntegrations();
      const updatedIntegrations = currentDefaultIntegrations.map(integration => {
        const status = mergedStatuses.find(s => s.id === integration.id);
        const mappedIntegration = {
          ...integration,
          connected: status?.connected || false,
          accountName: status?.accountName,
          lastSyncedAt: status?.lastSyncedAt,
          connectedAt: status?.connectedAt,
          error: status?.error // Include error/warning for display
        };
        
        // Log QuickBooks specifically for debugging
        if (integration.id === 'quickbooks') {
          console.log('QuickBooks integration mapped:', {
            connected: mappedIntegration.connected,
            accountName: mappedIntegration.accountName,
            lastSyncedAt: mappedIntegration.lastSyncedAt,
            error: mappedIntegration.error,
            originalStatus: status
          });
        }
        
        return mappedIntegration;
      });
      
      setIntegrations(updatedIntegrations);
    } catch (error) {
      console.error('Error loading integration statuses:', error);
      // Fallback to default integrations (current enabled integrations)
      setIntegrations(getDefaultIntegrations());
    } finally {
      isLoadingStatusesRef.current = false;
      if (!skipLoadingState) {
        setIsLoading(false);
      }
    }
  }, [communityId, userId]);

  const handleOAuthCallback = useCallback(async (integrationId: string, code: string, realmId?: string) => {
    try {
      console.log('handleOAuthCallback called:', { integrationId, code: code ? 'present' : 'missing', realmId, communityId, userId });
      
      if (integrationId === 'quickbooks') {
        if (!realmId) {
          console.error('QuickBooks OAuth callback missing realmId!');
          // Backend will return specific error for missing realmId, but we can catch it early
          toast.error('QuickBooks connection failed: Missing realm ID. Please try again.');
          setSearchParams({});
          return;
        }
        
        // IMPORTANT: Use the same redirect URI that was used in the initiate request
        // This must match exactly to avoid "Redirect URI mismatch" errors
        const redirectUri = `${window.location.origin}/settings/integrations?type=quickbooks`;
        
        console.log('Completing QuickBooks OAuth:', { communityId, userId, code: code ? 'present' : 'missing', realmId, redirectUri });
        await completeQuickBooksOAuth(communityId, userId, code, realmId, redirectUri);
        // Note: QuickBooks OAuth via popup shows toast in QuickBooksIntegration component
        // Only show toast here if not in popup context (direct URL callback)
        if (!window.opener) {
          toast.success('QuickBooks connected successfully!');
        }
      } else if (integrationId === 'google_calendar') {
        await completeGoogleCalendarOAuth(communityId, userId, code);
        toast.success('Google Calendar connected successfully!');
      } else if (integrationId === 'google_drive') {
        await completeGoogleDriveOAuth(communityId, userId, code);
        toast.success('Google Drive connected successfully!');
      } else if (integrationId === 'mailchimp') {
        await completeMailchimpOAuth(communityId, userId, code);
        toast.success('Mailchimp connected successfully!');
      } else if (integrationId === 'hubspot') {
        await completeHubSpotOAuth(communityId, userId, code);
        toast.success('HubSpot connected successfully!');
      }
      
      // Wait a moment for backend to process
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Reload statuses and close modal
      await loadIntegrationStatuses();
      setIsModalOpen(false);
      setSelectedIntegration(null);
      setSearchParams({}); // Clean up URL
    } catch (error: any) {
      console.error('Error completing OAuth:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      
      // Backend now returns standardized error format: {"error": "message"}
      // Backend returns specific, user-friendly error messages that should be displayed directly
      // Examples:
      // - "Authorization code expired or already used. Please try connecting again."
      // - "Invalid QuickBooks credentials. Please contact support."
      // - "Redirect URI mismatch. Please contact support."
      // - "Network timeout while exchanging authorization code. Please try connecting again."
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to complete QuickBooks connection';
      
      // Display backend error message directly - it's already user-friendly
      // Only add prefix if it's a generic error
      let userMessage = errorMessage;
      if (!errorMessage.includes('Authorization code') && 
          !errorMessage.includes('Invalid QuickBooks') && 
          !errorMessage.includes('Redirect URI') && 
          !errorMessage.includes('Network') &&
          !errorMessage.includes('timeout') &&
          !errorMessage.includes('Missing realmId')) {
        // Generic error - add context
        userMessage = `Failed to complete connection: ${errorMessage}`;
      }
      
      // Only show error if not in popup context (popup handles its own errors via message handler)
      // This prevents duplicate error messages when OAuth is handled via popup
      if (!window.opener) {
        toast.error(userMessage);
      }
    }
  }, [communityId, userId, loadIntegrationStatuses, setSearchParams]);

  // Load integration statuses on mount
  useEffect(() => {
    if (communityId && userId && !isLoadingStatusesRef.current) {
      loadIntegrationStatuses();
    }
  }, [communityId, userId, loadIntegrationStatuses]); // loadIntegrationStatuses is a useCallback with [communityId, userId] deps, so it's stable

  // Listen for QuickBooks status updates from modal
  useEffect(() => {
    const handleQuickBooksUpdate = () => {
      // Only handle if not already handled recently
      if (statusUpdateHandledRef.current) {
        console.log('IntegrationsSettings: Skipping quickbooksStatusUpdated - already handled');
        return;
      }
      
      statusUpdateHandledRef.current = true;
      console.log('IntegrationsSettings: Handling quickbooksStatusUpdated event');
      
      // Refresh integration statuses when QuickBooks status changes (silent refresh)
      loadIntegrationStatuses(true);
      
      // Reset flag after delay to allow future updates
      setTimeout(() => {
        statusUpdateHandledRef.current = false;
      }, 2000);
    };

    window.addEventListener('quickbooksStatusUpdated', handleQuickBooksUpdate);
    return () => {
      window.removeEventListener('quickbooksStatusUpdated', handleQuickBooksUpdate);
    };
  }, [loadIntegrationStatuses]);

  // Handle OAuth callback from URL
  // Support both old format (?integration=...) and new format (?type=...) for backward compatibility
  useEffect(() => {
    const integrationParam = searchParams.get('type') || searchParams.get('integration'); // New format: type, fallback to old: integration
    const code = searchParams.get('code');
    const realmId = searchParams.get('realmId'); // For QuickBooks

    // If we're in a popup window (OAuth callback), post message to parent and close
    if (window.opener && integrationParam && code) {
      console.log('OAuth callback detected in popup, posting message to parent:', { integrationParam, code: code ? 'present' : 'missing', realmId });
      
      if (integrationParam === 'quickbooks') {
        if (realmId) {
          // Post message to parent window with OAuth parameters
          window.opener.postMessage({
            type: 'quickbooks-oauth-callback',
            code,
            realmId
          }, window.location.origin);
          
          // Show success message and close popup after a short delay
          setTimeout(() => {
            window.close();
          }, 1000);
        } else {
          // Check for error parameters
          const error = searchParams.get('error');
          const errorDescription = searchParams.get('error_description');
          
          console.error('QuickBooks OAuth callback error or missing realmId in popup:', { error, errorDescription, realmId });
          window.opener.postMessage({
            type: 'quickbooks-oauth-error',
            error: errorDescription || error || 'Missing realmId parameter'
          }, window.location.origin);
          setTimeout(() => {
            window.close();
          }, 1000);
        }
        return;
      }
    }

    // Normal OAuth callback handling (not in popup)
    // Skip if we're in a popup context - popup handles OAuth via message handler
    // Also skip QuickBooks if we're in popup context to avoid duplicate handling
    if (integrationParam && code && communityId && userId && !window.opener) {
      // For QuickBooks, only handle if not in popup (popup uses message handler)
      if (integrationParam === 'quickbooks') {
        // QuickBooks OAuth is handled via popup message handler in QuickBooksIntegration component
        // Only handle here if not in popup context
        handleOAuthCallback(integrationParam, code, realmId || undefined);
      } else {
        handleOAuthCallback(integrationParam, code, realmId || undefined);
      }
    } else if (integrationParam && !window.opener) {
      // Open modal if integration param exists but no code (user clicked connect)
      // Only if not in popup context
      setSelectedIntegration(integrationParam);
      setIsModalOpen(true);
      // Clean up URL
      setSearchParams({});
    }
  }, [searchParams, communityId, userId, handleOAuthCallback, setSearchParams]);

  const handleIntegrationClick = (integrationId: string) => {
    setSelectedIntegration(integrationId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedIntegration(null);
    // Only reload if we haven't already handled a status update recently
    // This prevents duplicate reloads when the event was already handled
    if (!statusUpdateHandledRef.current) {
      console.log('IntegrationsSettings: Reloading statuses on modal close');
      loadIntegrationStatuses();
    } else {
      console.log('IntegrationsSettings: Skipping reload on modal close - status update already handled');
    }
  };

  const handleDisconnectQuickBooks = () => {
    setShowDisconnectModal(true);
  };

  const handleConfirmDisconnectQuickBooks = async () => {
    setShowDisconnectModal(false);

    try {
      await disconnectQuickBooks(communityId, userId);
      toast.success('QuickBooks disconnected successfully');
      await loadIntegrationStatuses();
    } catch (error) {
      console.error('Error disconnecting QuickBooks:', error);
      toast.error('Failed to disconnect QuickBooks');
    }
  };
  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
  <>
  <div className="flex flex-row bg-white h-[75px] w-full border-t-[#E6E6E6] border-t-[1px]">
      <p className="mx-[24px] my-auto font-open-sans text-[16px] text-[#4D4D4D]/70">
      Connect your favorite tools to your dashboard and manage them all in one place.
      </p>
  </div> 
  <div className="flex flex-col w-11/12 mx-auto py-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
      {
        integrations.map((integration, i) => {
          return (
            <IntegrationCard 
              key={i} 
              id={integration.id} 
              name={integration.name} 
              description={integration.description} 
              icon={integration.icon} 
              url={integration.url} 
              connected={integration.connected}
              accountName={integration.accountName}
              lastSyncedAt={integration.lastSyncedAt}
              connectedAt={integration.connectedAt}
              onClick={() => handleIntegrationClick(integration.id)}
              onDisconnect={integration.id === 'quickbooks' ? handleDisconnectQuickBooks : undefined}
            />
          )
        })
      }
    </div>
  </div>
  {selectedIntegration && (
    <IntegrationModal
      isOpen={isModalOpen}
      integrationId={selectedIntegration}
      communityId={communityId}
      userId={userId}
      onClose={handleCloseModal}
      onStatusChange={() => loadIntegrationStatuses(true)}
    />
  )}
  <DisconnectConfirmationModal
    isOpen={showDisconnectModal}
    integrationName="QuickBooks"
    onClose={() => setShowDisconnectModal(false)}
    onConfirm={handleConfirmDisconnectQuickBooks}
  />
  </>
  )
}

type IntegrationCardProps = IntegrationType & {
  onClick: () => void;
  onDisconnect?: () => void;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({id, name, description, icon: Icon, url, connected, accountName, lastSyncedAt, connectedAt, onClick, onDisconnect}) => {
  const isQuickBooks = id === 'quickbooks';
  
  return (
    <div className="flex flex-col bg-white rounded-lg shadow-xs border-0 border-[#E9E9E9]/90  transition-shadow duration-200 h-full">
      <div className="flex flex-col p-6 h-full">
        {/* Header: Icon, Name, and Status */}
        <div className="flex flex-row items-start gap-3 mb-4">
          <div className="flex-shrink-0">
            <Icon className="w-12 h-12 rounded-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 className="text-lg font-semibold font-raleway text-black truncate">{name}</h3>
              <div className={`flex-shrink-0 ${connected ? "text-green-700 bg-green-50 border border-green-200" : "text-[#999999] bg-[#FAFAFA] border border-[#E9E9E9]"} px-3 py-1 rounded-full text-xs font-medium`}>
                {connected ? "Connected" : "Not Connected"}
              </div>
            </div>
          </div>
        </div>
        
        {/* Show QuickBooks connection details when connected */}
        {/* {isQuickBooks && connected && (
          <div className="mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              {accountName ? (
                <>
                  <p className="text-sm text-green-800 font-medium">
                    <strong>Account:</strong> {accountName}
                  </p>
                  {lastSyncedAt && (
                    <p className="text-xs text-green-700 mt-1.5">
                      <strong>Last Synced:</strong> {new Date(lastSyncedAt).toLocaleString()}
                    </p>
                  )}
                  {connectedAt && !lastSyncedAt && (
                    <p className="text-xs text-green-700 mt-1.5">
                      <strong>Connected:</strong> {new Date(connectedAt).toLocaleString()}
                    </p>
                  )}
                </>
              ) : (
                <>
                  {lastSyncedAt ? (
                    <p className="text-sm text-green-800 font-medium">
                      <strong>Last Synced:</strong> {new Date(lastSyncedAt).toLocaleString()}
                    </p>
                  ) : connectedAt ? (
                    <p className="text-sm text-green-800 font-medium">
                      <strong>Connected:</strong> {new Date(connectedAt).toLocaleString()}
                    </p>
                  ) : (
                    <p className="text-sm text-green-800 font-medium">
                      QuickBooks account is connected
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )} */}
        
        {/* Description */}
        <p className="text-sm font-open-sans text-[#4D4D4D]/80 mb-6 flex-1 leading-relaxed">{description}</p>
        
        {/* Action Buttons */}
        <div className="flex flex-row gap-3 mt-auto">
          <button 
            className={`${connected ? "text-white bg-primary-200/80 hover:bg-primary-300" : " border border-[#E9E9E9]/90 text-primary-200 bg-white hover:bg-primary-200 hover:text-white"} flex flex-row items-center justify-center h-11 flex-1 transition-all duration-200 px-4 py-2 rounded-lg font-raleway font-semibold text-sm`}
            onClick={onClick}
          >
            <LinkIcon className="w-4 h-4 mr-2" />
            {connected ? "Manage" : `Connect ${name}`}
          </button>
          
          {/* Disconnect button for QuickBooks when connected */}
          {isQuickBooks && connected && onDisconnect && (
            <button 
              className={`px-4 py-2 text-sm font-semibold font-raleway text-red-600 hover:text-red-700 hover:bg-red-50 border-[1px] border-red-300/80 bg-white rounded-lg transition-all duration-200 h-11 whitespace-nowrap`}
              onClick={onDisconnect}
            >
              <LinkOffIcon className="w-4 h-4 mr-2" />
              Disconnect
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

type ProfileSettingsProps = {
  authUser: AccountSchema
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void
}

const ProfileSettings: React.FC<ProfileSettingsProps> = ({authUser}) => {
  const [userAccount, setUserAccount] = useState<AccountSchema>(authUser as AccountSchema)
  const [imageError, setImageError] = useState<boolean>(false)
  const [userAvatar, setUserAvatar] = useState<string>(authUser?.avatar ? authUser.avatar : "")
  
  const { getAccountRole } = useManageTeam()
  const accountRole = getAccountRole(userAccount)

  const { updateAccountProfile } = useLoadAuthUser()

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const imgType = event.target.name
    if (file) {
      // Read the selected image file as a data URL
      const reader = new FileReader();
      reader.onload = (e) => {
        if (imgType === "avatar") {
          setUserAvatar(e.target?.result as string);
        }
      };
      reader.readAsDataURL(file);
    } else {
      if (imgType === "avatar") {
        setUserAvatar("");
      }
    }
    handleFileChange(event);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = event.target;
    if (name && files && files.length > 0) {
      let file_data = files[0];
      setUserAccount(prev => ({
        ...prev,
        [name]: file_data,
      }))
    }
  }

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name && (value || value === "")) {
      setUserAccount(prev => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const handleSaveChangesForm = async () => {
    // console.log("userAccount", userAccount)
    const changes = getChangedAttributes<AccountSchema>(authUser as AccountSchema, userAccount as AccountSchema)
    console.log("changes", changes)
    if (Object.keys(changes).length > 0 && authUser.username){
      const result = await updateAccountProfile(authUser.username as string, changes)
      if (result){
        toast.success("Profile updated successfully")
      } else {
        toast.error("Failed to update profile. Please try again.")
      }
    }
  }

  // console.log("userAccount", userAccount)
  // console.log("authUser", authUser)

  return (
    <>
      <div className="flex flex-row bg-white h-[75px] w-full border-t-[#E6E6E6] border-t-[1px]">
        <p className="mx-[24px] my-auto font-open-sans text-[16px] text-[#4D4D4D]/70">
          View and edit your profile.
        </p>
        {
          (!areObjectsEqual(userAccount, authUser)) ? (
          <div className="flex ml-auto mx-[24px] h-auto font-open-sans">
            <button 
                className="text-primary-200 font-open-sans h-[43px] px-2 border-[1px] border-[#E9E9E9] ml-auto rounded-lg my-auto text-[16px]"
                onClick={handleSaveChangesForm}
              >
                Save Changes
            </button>
            {/* Add Cancel button and reset to api data */}
          </div>
          ) : (
            <></>
          )
        }
      </div>
      
      <div className="flex flex-col w-11/12 mx-auto">
        <div className="flex flex-col bg-white my-3 h-auto w-full rounded-lg">
          <div className="flex flex-col mx-[24px] my-auto p-6">
            {/* Top Section - User Identification and Profile Picture Management */}
            <div className="flex flex-row items-start mb-8">
              {/* Profile Picture/Logo */}
              {

                userAvatar && !imageError ? (
                  <div className="flex flex-row items-start mr-6">
                    <img
                        src={userAvatar}
                        alt="User Avatar"
                        className="w-[56px] h-[56px] rounded-full object-fit object-center"
                        onError={() => setImageError(true)}
                    />
                  </div>
                ) : (
                  <UserAvatar
                      firstName={userAccount?.first_name}
                      lastName={userAccount?.last_name}
                      username={userAccount?.username}
                      email={userAccount?.email}
                      size="lg"
                      className="w-[56px] h-[56px] mr-6"
                  />
                )
              }
              
              {/* User Greeting and Role */}
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <h1 className="text-xl font-semibold text-black mr-3">Hi {userAccount?.first_name}</h1>
                  <span className="bg-[#F5F5F5] border border-[#E9E9E9]/90 font-raleway font-medium rounded-md px-3 py-1 text-sm text-black">
                    {capitalizeString(accountRole)}
                  </span>
                </div>
                
                {/* Email Address */}
                <p className="text-[#4D4D4D]/70 font-open-sans text-base mb-4">{userAccount?.email}</p>
                
                {/* Profile Picture Action Buttons */}
                <div className="flex space-x-3">
                  {/* <button className="bg-primary-200 text-white px-4 py-2 rounded-md text-sm font-medium">
                    Update Profile Photo
                  </button> */}

                  <label htmlFor="image-input-avatar" className="text-[14px] font-raleway hover:text-primaryDark-300 cursor-pointer bg-primary-200 text-white px-4 py-2 rounded-md text-sm font-medium">
                    Update Profile Photo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="image-input-avatar"
                      name="avatar"
                      data-testid="avatar-input"
                    />
                  </label>
                  <button onClick={()=>{
                      setUserAvatar("");
                      setUserAccount(prev => ({
                        ...prev,
                        avatar: ""
                      }))
                    }} className="bg-white border border-[#E9E9E9]/90 text-red-600 px-4 py-2 rounded-md text-sm font-medium">
                    Remove Profile Photo
                  </button>
                </div>
              </div>
            </div>
            
            {/* Bottom Section - User Details Form */}
            <div className="space-y-6">
              {/* First Name and Last Name - Side by Side */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-500 text-sm font-normal mb-2">
                    First name
                  </label>
                  <input 
                    type="text" 
                    name="first_name"
                    className="w-full h-12 bg-white border border-[#E9E9E9]/90 rounded-md px-3 text-black text-base"
                    value={userAccount?.first_name}
                    // disabled={!!authUser?.first_name}
                    onChange={handleFormChange}
                  />
                </div>
                <div>
                  <label className="block text-gray-500 text-sm font-normal mb-2">
                    Last name
                  </label>
                  <input 
                    type="text" 
                    name="last_name"
                    className="w-full h-12 bg-white border border-[#E9E9E9]/90 rounded-md px-3 text-black text-base"
                    value={userAccount?.last_name}
                    // disabled={!!authUser?.last_name}
                    onChange={handleFormChange}
                  />
                </div>
              </div>
              
              {/* Email - Full Width */}
              <div>
                <label className="block text-gray-500 text-sm font-normal mb-2">
                  Email Address
                </label>
                <input 
                  type="email" 
                  name="email"
                  className="w-full h-12 bg-white border border-[#E9E9E9]/90 rounded-md px-3 text-black text-base"
                  value={userAccount?.email}
                  disabled={!!authUser?.email}
                  onChange={handleFormChange}
                />
              </div>
              
              {/* Role - Full Width */}
              <div>
                <label className="block text-gray-500 text-sm font-normal mb-2">
                  Role
                </label>
                <select value={accountRole} name="role" disabled className="w-full h-12 bg-white border border-[#E9E9E9]/90 rounded-md px-3 flex items-center text-black text-base">
                  <option disabled={accountRole !== "viewer"} value="viewer">Viewer</option>
                  <option disabled={accountRole !== "editor"}  value="editor">Editor</option>
                  <option disabled={accountRole !== "admin"} value="admin">Admin</option>
                  <option disabled={accountRole !== "owner"} value="owner">Owner</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}



  type ResourceOfferedSettingsProps = {
  community: DSCommunitySchema
  currentCommunity: DSCommunitySchema
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void
}

export const ResourceOfferedSettings: React.FC<ResourceOfferedSettingsProps> = ({community, currentCommunity}) => {
  const communityId = community?.id ? community.id : ""
  var { isUpdateSuccess, isUpdateError, isUpdatePending } = useLoadCommunity(communityId)
  const [isConfirmBoxOpen, toggleConfirmBox] = useState<boolean>(!isUpdatePending && (isUpdateSuccess || isUpdateError))

  type ResourceSelectedType = {
    [K in ResourceType]: boolean;
  }

  const defaultResourceSelected = {
    kitchen: false,
    documentation: false,
    training: false,
    funding: false,
    expert: false,
    farm: false,
  }
  const [resourceSelected, updateResourceSelect] = useState<ResourceSelectedType>(currentCommunity?.preferences ? currentCommunity.preferences as ResourceSelectedType : defaultResourceSelected);

  const { updatePreferencesData, isLoading, isError, error, isSuccess } = useUpdatePreferences()

  useEffect(() => {
    if (isSuccess){
      toast.success("Successfully updated preferences")
    } else if (isError || error){
      toast.error("Update failed: " + (error?.message || "An error occurred"))
    }
  }, [isSuccess, isError, error])

  type ResourceOptionType = {
    id: ResourceType
    name: string
  }

  const resourceOptions: ResourceOptionType[] = [
    {
      id: "kitchen",
      name: "Local Kitchen Spaces"
    },
    {
      id: "documentation",
      name: "Required Documentation"
    },
    {
      id: "training",
      name: "Culinary Training"
    },
    {
      id: "funding",
      name: "Funding Opportunities"
    },
    {
      id: "expert",
      name: "Industry Experts"
    },
    {
      id: "farm",
      name: "Local Fresh Food"
    }
  ]

  const handleSaveChanges = () => {
    if (Object.keys(resourceSelected).length > 0){
      updatePreferencesData(resourceSelected)
    }
  }

  useEffect(() => {
    document.title = `HomeCook | Resource Offered Settings`;
    // eslint-disable-next-line
  }, [community]);

  if (isLoading || isUpdatePending){
    return <LoadingScreen />
  }


  return (
    <>
      <div className="flex flex-row bg-white h-[75px] w-full border-t-[#E6E6E6] border-t-[1px]">
        <p className="mx-[24px] my-auto font-open-sans text-[16px] text-[#4D4D4D]/70">
          Choose which resources to display in the resources section.
        </p>
        
        {/* {
          isError ? (
            <div className="flex m-auto h-auto bg-red-100 rounded-sm font-raleway font-normal text-[16px] text-red-600 py-2 px-4">
              <p className="text-red-600">Update failed: {error?.message || "An error occurred"}</p>
            </div>
          ) : (
            <></>
          )
        }

        {
          isSuccess ? (
            <div className="flex m-auto h-auto bg-green-100 rounded-sm font-raleway font-normal text-[16px] text-green-600 py-2 px-4">
              <p className="text-green-600">Successfully updated preferences</p>
            </div>
          ) : (
            <></>
          )
        } */}
    
        {
          (
            !areObjectsEqual(currentCommunity?.preferences ? currentCommunity.preferences as ResourceSelectedType : defaultResourceSelected, resourceSelected)
          ) ? (
            <div className="flex ml-auto mx-[24px] h-auto">
              <button
                className="text-primary-200 font-open-sans h-[43px] px-2 border-[1px] border-[#E9E9E9] ml-auto rounded-lg my-auto text-[16px]"
                onClick={handleSaveChanges}
              >
                Save Changes
              </button>
            </div>
          ) : (
            <></>
          )
        }
      </div> 


      <div className="flex flex-col w-11/12 mx-auto">
        <div className="flex flex-col bg-white my-3 h-auto w-full rounded-lg">

          <div className="flex flex-col mx-[24px] p-2 my-4">
            <p className="text-black font-raleway font-semibold text-[18px] my-1">
              Resources to Offer
            </p>
            {/* <p className="text-[#4D4D4D] text-[16px] font-open-sans mt-1 mb-4 font-light ">Choose which resources to display in the resources section.</p> */}
            {/* <input name="name" onChange={handleChange} className="border-[#E9E9E9] h-[51px] border-[1px] w-full rounded-md" value={community.name} /> */}


            <hr className="h-[1px] border-[#E9E9E9]" />
            {
              resourceOptions.map((option, i) => (
                <div key={i} className="flex flex-row font-medium border-0 border-b-[1px] py-3 border-[#E9E9E9] text-[#333333] text-[16px] font-raleway">
                  {option.name}

                  {
                    resourceSelected[option.id as ResourceType] ? (
                      <CheckBoxIcon
                        data-testid={`resource-offered-${option.id}`}
                        className="ml-auto text-primary-300 cursor-pointer"
                        onClick={() => updateResourceSelect(prev => ({...prev, [option.id]: false}))}
                      />
                    ) : (
                      <CheckBoxOutlineBlankIcon
                        data-testid={`resource-offered-${option.id}`}
                        className="cursor-pointer ml-auto text-[#CFCFCF]"
                        onClick={() => updateResourceSelect(prev => ({...prev, [option.id]: true}))}
                      />
                    )
                  }
                </div>
              ) )
            }
          </div>
        </div>
      </div>
      
      <ConfirmationModal  
        isOpen={isConfirmBoxOpen} 
        message={isUpdateSuccess ? "All changes have been saved": "Update Failed"} 
        closeModal={()=>toggleConfirmBox(false)}
      />
    </>
  )
}


type NotificationSettingsProps = {
  community: DSCommunitySchema
  currentCommunity: DSCommunitySchema
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({community, currentCommunity}) => {
  const communityId = community?.id ? community.id : ""
  var { isUpdateSuccess, isUpdateError, isUpdatePending } = useLoadCommunity(communityId)
  const [isConfirmBoxOpen, toggleConfirmBox] = useState<boolean>(!isUpdatePending && (isUpdateSuccess || isUpdateError))
  
  type OptionType = "credential_uploads" | "membership_requests" | "resource_requests" | "booking_reviews"
  type SelectedType = {
    [K in OptionType]: boolean;
  }

  const defaultSelected = {
    credential_uploads: false,
    membership_requests: true,
    resource_requests: true,
    booking_reviews: false,
  }

  const [selected, updateSelect] = useState<SelectedType>(defaultSelected);

  const { isLoading, isError, error, isSuccess } = useUpdatePreferences()

  useEffect(() => {
    if (isSuccess){
      toast.success("Successfully updated preferences")
    } else if (isError || error){
      toast.error("Update failed: " + (error?.message || "An error occurred"))
    }
  }, [isSuccess, isError, error])

  type NotificationOptionType = {
    id: OptionType
    name: string
  }

  const notificationOptions: NotificationOptionType[] = [
    {
      id: "membership_requests",
      name: "Membership Requests"
    },
    {
      id: "credential_uploads",
      name: "Credential Uploads"
    },
    {
      id: "resource_requests",
      name: "Resource Requests"
    },
    {
      id: "booking_reviews",
      name: "Booking Reviews "
    }
  ]

  useEffect(() => {
    document.title = `HomeCook | Notification Settings`;
    // eslint-disable-next-line
  }, [community]);

  if (isLoading || isUpdatePending){
    return <LoadingScreen />
  }


  return (
    <>
      <div className="flex flex-row bg-white h-[75px] w-full border-t-[#E6E6E6] border-t-[1px]">
        <p className="mx-[24px] my-auto font-open-sans text-[16px] text-[#4D4D4D]/70">
            View and edit your notifications.
        </p>
    
        {
          (
            !areObjectsEqual(defaultSelected, selected) 
          ) ? (
            <div className="flex ml-auto mx-[24px] h-auto">
              <button
                className="text-primary-200 font-open-sans h-[43px] px-2 border-[1px] border-[#E9E9E9] ml-auto rounded-lg my-auto text-[16px]"
                // onClick={handleSaveChanges}
              >
                Save Changes
              </button>
            </div>
          ) : (
            <></>
          )
        }
      </div> 

      <div className="flex flex-col w-11/12 mx-auto">
        <div className="flex flex-col bg-white my-3 h-[346px] w-full rounded-lg">

          <div className="flex flex-col mx-[24px] my-4 p-2">
            <p className="text-black font-raleway font-semibold text-[18px] my-1">
                Email notifications
            </p>
            <p className="text-[#4D4D4D] text-[16px] font-open-sans mt-1 mb-4 font-light ">Get notified about your community and resource updates.</p>
            {/* <input name="name" onChange={handleChange} className="border-[#E9E9E9] h-[51px] border-[1px] w-full rounded-md" value={community.name} /> */}


            <hr className="h-[1px] border-[#E9E9E9]" />
            {
              notificationOptions.map((option, i) => (
                <div key={i} className="flex flex-row font-medium border-0 border-b-[1px] py-3 border-[#E9E9E9] text-[#333333] text-[16px] font-raleway">
                  {option.name}

                  {
                    (selected[option.id as OptionType]) ? (
                      <CheckBoxIcon
                        className="cursor-pointer ml-auto text-primary-300"
                        onClick={() => updateSelect(prev => ({...prev, [option.id]: false}))}
                      />
                    ) : (
                      <CheckBoxOutlineBlankIcon
                        className="cursor-pointer ml-auto text-[#CFCFCF]"
                        onClick={() => updateSelect(prev => ({...prev, [option.id]: true}))}
                      />
                    )
                  }
                </div>
              ) )
            }

          </div>
        </div>
      </div>

      <ConfirmationModal  
        isOpen={isConfirmBoxOpen} 
        message={isUpdateSuccess ? "All changes have been saved": "Update Failed"} 
        closeModal={()=>toggleConfirmBox(false)}
      />
    </>
  )
}




export default SettingsPage;